import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";
import "./admin.css";
import "./EditBike.css";

function EditBike() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [bike, setBike] = useState({
    name: "", type: "", pricePerDay: "",
    hourlyPrice: "", weekendPrice: "",
    engineCapacity: "", weight: "", mileage: "", fuelTankCapacity: "",
    totalUnits: "1",
  });
  const [colors, setColors] = useState([]);
  const [colorInput, setColorInput] = useState("#000000");
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  // Load existing bike details
  useEffect(() => {
    setFetchLoading(true);
    axiosClient
      .get(`/bikes/${id}`)
      .then((res) => {
        const d = res.data;
        setBike({
          name:             d.name           ?? "",
          type:             d.type           ?? "",
          pricePerDay:      d.pricePerDay    ?? "",
          hourlyPrice:      d.hourlyPrice    ?? "",
          weekendPrice:     d.weekendPrice   ?? "",
          engineCapacity:   d.engineCapacity ?? "",
          weight:           d.weight         ?? "",
          mileage:          d.mileage        ?? "",
          fuelTankCapacity: d.fuelTankCapacity ?? "",
          totalUnits:       d.totalUnits     ?? 1,
        });
        setColors(d.colors ?? []);
      })
      .catch(() => toast.error("Failed to load bike details."))
      .finally(() => setFetchLoading(false));
  }, [id]);

  const handleChange = (e) =>
    setBike({ ...bike, [e.target.name]: e.target.value });

  const addColor = () => {
    if (!colors.includes(colorInput)) setColors([...colors, colorInput]);
  };
  const removeColor = (c) => setColors(colors.filter((x) => x !== c));

  const updateBike = (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("name",         bike.name);
    formData.append("type",         bike.type);
    formData.append("pricePerDay",  bike.pricePerDay);
    formData.append("hourlyPrice",  bike.hourlyPrice  || 0);
    formData.append("weekendPrice", bike.weekendPrice || 0);
    formData.append("engineCapacity",   bike.engineCapacity   || "");
    formData.append("weight",           bike.weight           || "");
    formData.append("mileage",          bike.mileage          || "");
    formData.append("fuelTankCapacity", bike.fuelTankCapacity || "");
    formData.append("totalUnits",       bike.totalUnits       || 1);
    colors.forEach((c) => formData.append("colors[]", c));
    if (image) formData.append("image", image);

    axiosClient
      .put(`/bikes/update/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(() => {
        toast.success("Bike updated successfully!");
        navigate("/admin/bikes");
      })
      .catch(() => toast.error("Update failed. Please try again."))
      .finally(() => setLoading(false));
  };

  if (fetchLoading) return (
    <div className="admin-spinner-wrap">
      <div className="admin-spinner-ring" />
      <p className="admin-spinner-label">Loading bike details…</p>
    </div>
  );

  return (
    <>
      {loading && (
        <div className="admin-submit-overlay">
          <div className="admin-spinner-ring" />
          <p className="admin-spinner-label">Saving changes…</p>
        </div>
      )}

      <div className="admin-page-header">
        <h1>Edit Bike</h1>
        <p>Update the details for this bike</p>
      </div>

      <div className="add-bike-container">
        <div className="add-bike-form-card">
          <form onSubmit={updateBike}>
            <div className="add-bike-grid">

              {/* ── Core Details ── */}
              <div className="add-bike-section-title full-width">🏍️ Core Details</div>

              <div className="admin-form-group">
                <label>Bike Name <span className="req">*</span></label>
                <input
                  className="admin-input" name="name"
                  placeholder="e.g. Honda Activa 6G"
                  value={bike.name} onChange={handleChange} required
                />
              </div>

              <div className="admin-form-group">
                <label>Type <span className="req">*</span></label>
                <input
                  className="admin-input" name="type"
                  placeholder="e.g. Scooter, Cruiser"
                  value={bike.type} onChange={handleChange} required
                />
              </div>

              <div className="admin-form-group">
                <label>Total Units Available</label>
                <input
                  className="admin-input" name="totalUnits" type="number" min="1"
                  placeholder="e.g. 3"
                  value={bike.totalUnits} onChange={handleChange}
                />
              </div>

              {/* ── Pricing ── */}
              <div className="add-bike-section-title full-width">💰 Pricing</div>

              <div className="admin-form-group">
                <label>Daily Price (₹) <span className="req">*</span></label>
                <input
                  className="admin-input" name="pricePerDay" type="number"
                  placeholder="e.g. 300"
                  value={bike.pricePerDay} onChange={handleChange} required
                />
              </div>

              <div className="admin-form-group">
                <label>Hourly Price (₹)</label>
                <input
                  className="admin-input" name="hourlyPrice" type="number"
                  placeholder="e.g. 50"
                  value={bike.hourlyPrice} onChange={handleChange}
                />
              </div>

              <div className="admin-form-group">
                <label>Weekend Price (₹)</label>
                <input
                  className="admin-input" name="weekendPrice" type="number"
                  placeholder="e.g. 350"
                  value={bike.weekendPrice} onChange={handleChange}
                />
              </div>

              {/* ── Specs ── */}
              <div className="add-bike-section-title full-width">🔧 Specifications</div>

              <div className="admin-form-group">
                <label>Engine Capacity</label>
                <input
                  className="admin-input" name="engineCapacity"
                  placeholder="e.g. 110 CC"
                  value={bike.engineCapacity} onChange={handleChange}
                />
              </div>

              <div className="admin-form-group">
                <label>Weight</label>
                <input
                  className="admin-input" name="weight"
                  placeholder="e.g. 102 Kgs"
                  value={bike.weight} onChange={handleChange}
                />
              </div>

              <div className="admin-form-group">
                <label>Mileage</label>
                <input
                  className="admin-input" name="mileage"
                  placeholder="e.g. 60 Kms"
                  value={bike.mileage} onChange={handleChange}
                />
              </div>

              <div className="admin-form-group">
                <label>Fuel Tank Capacity</label>
                <input
                  className="admin-input" name="fuelTankCapacity"
                  placeholder="e.g. 3.5 Litres"
                  value={bike.fuelTankCapacity} onChange={handleChange}
                />
              </div>

              {/* ── Colors ── */}
              <div className="add-bike-section-title full-width">🎨 Available Colours</div>

              <div className="admin-form-group full-width color-picker-group">
                <div className="color-picker-row">
                  <input
                    type="color"
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    className="admin-color-input"
                  />
                  <input
                    type="text"
                    className="admin-input"
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    placeholder="#000000"
                    style={{ maxWidth: 120 }}
                  />
                  <button type="button" className="btn btn-ghost add-color-btn" onClick={addColor}>
                    + Add
                  </button>
                </div>
                {colors.length > 0 && (
                  <div className="color-swatch-list">
                    {colors.map((c) => (
                      <div key={c} className="color-swatch-tag">
                        <span className="swatch-dot" style={{ background: c }} />
                        <span className="swatch-hex">{c}</span>
                        <button type="button" className="swatch-remove" onClick={() => removeColor(c)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Image ── */}
              <div className="add-bike-section-title full-width">📸 Replace Image (optional)</div>

              <div className="admin-form-group full-width">
                <input
                  className="admin-file-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImage(e.target.files[0])}
                />
              </div>

            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate("/admin/bikes")}
                style={{ flex: 1, justifyContent: "center", padding: 12 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ flex: 2, justifyContent: "center", padding: 12 }}
              >
                {loading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>

        <div className="add-bike-info-card">
          <h3>Editing Tips</h3>
          <ul>
            <li>Leave the image field empty to keep the existing photo.</li>
            <li>Spec fields are optional — leave blank if not applicable.</li>
            <li>Colour changes take effect immediately on the booking page.</li>
            <li>Pricing tiers (hourly/weekend) are shown to customers.</li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default EditBike;
